import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import RenFestSelfiePage from './pages/RenFestSelfiePage';

/* ⚠️ LAZY, AND THAT IS NOT AN OPTIMISATION DETAIL. The console pulls in
   @supabase/supabase-js; a static import would put it in the chunk a guest
   downloads on festival wifi to have their picture taken. Keep it lazy, and
   watch the chunk sizes `npm run build` prints. */
const Console = lazy(() => import('./pages/Console'));

/* The public face of the company: landing page and the two legal documents.
   Lazy for the same reason as the console — a guest at a festival downloads the
   booth and nothing else. */
const Home = lazy(() => import('./pages/public/Home'));
const Privacy = lazy(() => import('./pages/public/Privacy'));
const Terms = lazy(() => import('./pages/public/Terms'));

/* The page the link in a guest's delivery email lands on. Lazy for the same
   reason as the rest: this guest arrived from an email, not from a QR code, and
   the two never share a session. */
const Delivery = lazy(() => import('./pages/public/Delivery'));

/* The counter at booth #383. Lazy because a guest downloads the booth and
   nothing else, and this is staff-only. */
const Counter = lazy(() => import('./pages/Counter'));

/*
 * Stage 0 shell. The route shape is fixed NOW even though the tenant layer is
 * Stage 4, because a QR code is a physical object: once a code is printed the
 * URL behind it cannot change. See docs/DECISIONS.md D-02.
 *
 *   /p/:token                        a guest's delivered portrait
 *   /:tenantSlug/:eventSlug/counter   staff: find and print one portrait
 *   /:tenantSlug/:eventSlug          the booth
 *   /:tenantSlug/:eventSlug/ledger   the operator view  (Stage 1)
 *   /:tenantSlug/:eventSlug/pulse    the client dashboard (Stage 2)
 *   /console                         sign in; client and operator console
 *   /  /privacy  /terms              the public company pages
 *
 * The path SELECTS which config to load. It is never what a spend or a read is
 * scoped to - a guest can edit a path. Tenant and event resolve server-side
 * from the booth token on every privileged call.
 */

const Loading = (
  <main style={{ padding: '2rem', fontFamily: 'system-ui, sans-serif' }}>Loading…</main>
);

const lazyRoute = (El) => <Suspense fallback={Loading}><El /></Suspense>;

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={lazyRoute(Home)} />
        <Route path="/privacy" element={lazyRoute(Privacy)} />
        <Route path="/terms" element={lazyRoute(Terms)} />
        <Route path="/console" element={lazyRoute(Console)} />

        {/* ⚠️ /p/:token IS TWO SEGMENTS, EXACTLY LIKE THE BOOTH ROUTE BELOW.
            React Router v6 ranks a literal segment above a dynamic one, so this
            wins over /:tenantSlug/:eventSlug and `p` can never be a tenant slug
            again. That is not left to chance: sql/031 rejects `p` (and `api`,
            `console`, `privacy`, `terms`...) at the database. Adding any new
            two-segment route means adding its first word there in the same
            commit, or you have armed a trap for whoever onboards the tenant who
            wants that name. */}
        <Route path="/p/:token" element={lazyRoute(Delivery)} />

        <Route path="/:tenantSlug/:eventSlug" element={<RenFestSelfiePage />} />

        {/* ⚠️ UNDER THE TENANT AND EVENT, NOT AT THE ROOT, AND THAT IS A
            CORRECTNESS FIX RATHER THAN TIDINESS. This was `/counter` — a GLOBAL
            lookup for a PER-CLIENT job, and `recall.js` matched it by searching
            the whole of booth_run with no tenant filter. Invisible with one
            tenant; with two it shows a Shaman employee another client's guests.
            The route shape is what gave it away. Same family as the `/ledger`
            and `/pulse` routes in NAMING.md. */}
        <Route path="/:tenantSlug/:eventSlug/counter" element={lazyRoute(Counter)} />
        <Route path="*" element={lazyRoute(Home)} />
      </Routes>
    </BrowserRouter>
  );
}
